import React, { Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import Form, { Field, HelperMessage, RequiredAsterisk } from '@atlaskit/form';
import Button from '@atlaskit/button/new';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import Select, { type ValueType } from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import { Stack, Text } from '@atlaskit/primitives';

import { modalFormMessages } from './messages';

interface Option {
	label: string;
	value: string;
}

interface FeedbackModalProps {
	isOpen: boolean;
	closeModal: () => void;
	importSource: string;
}

// TODO: Translate this and move to a shared location
const NOTION_DATA_FORMAT_OPTIONS = [
	{ label: '@mentions', value: 'mentions' },
	{ label: 'Audio (embedded link)', value: 'audio_embedded_link' },
	{ label: 'Attachments', value: 'attachments' },
	{ label: 'Block equation', value: 'block_equation' },
	{ label: 'Breadcrumb', value: 'breadcrumb' },
	{ label: 'Bulleted lists', value: 'bulleted_lists' },
	{ label: 'Button', value: 'button' },
	{ label: 'Callout', value: 'callout' },
	{ label: 'Code block', value: 'code_block' },
	{ label: 'Code - Mermaid (diagram)', value: 'code_mermaid' },
	{ label: '2 columns', value: 'two_columns' },
	{ label: 'Customized text', value: 'customized_text' },
	{ label: 'Database child page properties', value: 'database_child_properties' },
	{ label: 'Dates', value: 'dates' },
	{ label: 'Dividers', value: 'dividers' },
	{ label: 'Embed (e.g. Figma)', value: 'embed' },
	{ label: 'Emojis', value: 'emojis' },
	{ label: 'Header images', value: 'header_images' },
	{ label: 'Headings', value: 'headings' },
	{ label: 'Images', value: 'images' },
	{ label: 'Inline equation', value: 'inline_equation' },
	{ label: 'Link to page', value: 'link_to_page' },
	{ label: 'Numbered lists', value: 'numbered_lists' },
	{ label: 'Page comments', value: 'page_comments' },
	{ label: 'Page emojis', value: 'page_emojis' },
	{ label: 'Quotes', value: 'quotes' },
	{ label: 'Reminder', value: 'reminder' },
	{ label: 'Static tables', value: 'static_tables' },
	{ label: 'Synced block', value: 'synced_block' },
	{ label: 'Table customization', value: 'table_customization' },
	{ label: 'Table data', value: 'table_data' },
	{ label: 'Table of contents', value: 'table_of_contents' },
	{ label: 'To-Do list', value: 'todo_list' },
	{ label: 'Toggle heading', value: 'toggle_heading' },
	{ label: 'Toggle list', value: 'toggle_list' },
	{ label: 'Video (embedded link)', value: 'video_embedded_link' },
	{ label: 'Web bookmark', value: 'web_bookmark' },
	{ label: 'Other', value: 'other' },
];

export const FeedbackModal = ({ isOpen, closeModal, importSource }: FeedbackModalProps) => {
	const intl = useIntl();

	return (
		<>
			<ModalTransition>
				{isOpen && (
					<Modal onClose={closeModal} width="large" testId="import-feedback-modal">
						<Form onSubmit={() => {}}>
							{({ formProps }) => (
								<form {...formProps} id="feedback-modal-form">
									<ModalHeader hasCloseButton>
										<ModalTitle>
											<FormattedMessage {...modalFormMessages.modalTitle} />
										</ModalTitle>
									</ModalHeader>
									<ModalBody>
										<Stack space="space.150">
											<Text>
												<FormattedMessage {...modalFormMessages.requiredFieldsAsterisk} />
												<RequiredAsterisk />
											</Text>
											<Text>
												<FormattedMessage {...modalFormMessages.feedbackAppreciation} />
											</Text>
											<Text>
												<FormattedMessage
													{...modalFormMessages.feedbackText}
													values={{
														source: importSource,
													}}
												/>
											</Text>

											<Field<ValueType<Option>>
												name="data-format"
												label={<FormattedMessage {...modalFormMessages.dataFormatLabel} />}
												isRequired
												aria-required
												testId="data-format-select"
											>
												{({ fieldProps: { id, ...rest } }) => (
													<Fragment>
														<Select<Option>
															inputId={id}
															{...rest}
															options={NOTION_DATA_FORMAT_OPTIONS}
															menuPosition="fixed"
															isClearable
															clearControlLabel={intl.formatMessage(
																modalFormMessages.dataFormatClearControlLabel,
															)}
														/>
														<HelperMessage>
															<FormattedMessage {...modalFormMessages.dataFormatHelperText} />
														</HelperMessage>
													</Fragment>
												)}
											</Field>

											<Field
												label={
													<FormattedMessage {...modalFormMessages.tellUsAboutYourExperience} />
												}
												name="tell-us-about-your-experience"
												testId="tell-us-about-your-experience-textarea"
											>
												{({ fieldProps }: any) => (
													<Fragment>
														<TextArea
															{...fieldProps}
															placeholder={intl.formatMessage(
																modalFormMessages.tellUsAboutYourExperiencePlaceholder,
															)}
														/>
													</Fragment>
												)}
											</Field>
										</Stack>
									</ModalBody>
									<ModalFooter>
										<Button
											appearance="subtle"
											onClick={closeModal}
											testId="feedback-modal-close-button"
										>
											<FormattedMessage {...modalFormMessages.closeButton} />
										</Button>
										<Button
											appearance="primary"
											type="submit"
											testId="feedback-modal-submit-button"
										>
											<FormattedMessage {...modalFormMessages.feedbackButton} />
										</Button>
									</ModalFooter>
								</form>
							)}
						</Form>
					</Modal>
				)}
			</ModalTransition>
		</>
	);
};
